"use client";

import type { ReactNode } from "react";

import * as m from "motion/react-m";
import clsx from "clsx";
import { BaseLinkTracked } from "@ui/components/core/LinkTracked";
import type { EventName } from "@mw/shared/analyticsEventNames";

interface NudgeFooterProps {
  children: ReactNode;
  hidden?: boolean;
  link: {
    href: string;
    eventName: EventName;
    eventProperties?: Record<string, unknown>;
  };
  /** Tailwind gradient "from" class. Default: "tw-from-blue-50" */
  gradientFrom?: string;
  /** Tailwind gradient "to" class. Default: "tw-to-white" */
  gradientTo?: string;
}

/**
 * A simple full-width nudge CTA footer for demos.
 * Always renders to reserve layout space (no CLS). Uses a mask animation
 * to reveal/hide the content. The entire bar is a single clickable link.
 */
export function NudgeFooter({
  children,
  hidden = false,
  link,
  gradientFrom = "tw-from-blue-50",
  gradientTo = "tw-to-white",
}: NudgeFooterProps) {
  return (
    <m.div
      initial={false}
      animate={{ maskPosition: hidden ? "0% 100%" : "0% 0%" }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      style={{
        maskSize: "100% 300%",
        maskImage: "linear-gradient(to bottom, white 33.333333%, transparent 66.666666%)",
      }}
      aria-hidden={hidden}
      className={clsx(
        "tw-relative tw-z-10 tw-bg-gradient-to-t -tw-mt-[3rem] md:-tw-mt-14 tw-rounded-b-3xl -tw-mx-px",
        hidden && "tw-pointer-events-none",
        gradientFrom,
        gradientTo,
      )}
    >
      <div
        className='tw-absolute tw-inset-0 tw-rounded-b-3xl tw-border-0.5 tw-pointer-events-none tw-border-blue-200'
        style={{ maskImage: "linear-gradient(to top, white, transparent)" }}
      />
      <div
        className='tw-absolute tw-inset-0 tw-rounded-b-3xl tw-border-x-0.5 tw-border-gray-200 tw-pointer-events-none'
        style={{ maskImage: "linear-gradient(to top, transparent, white)" }}
      />
      <BaseLinkTracked
        href={link.href}
        eventName={link.eventName}
        eventProperties={link.eventProperties}
        tabIndex={hidden ? -1 : undefined}
        className='tw-flex tw-items-center tw-text-pretty tw-justify-center tw-px-5 tw-pt-5 md:tw-pt-6 tw-pb-4 tw-text-xs/[1rem] tw-text-center tw-text-blue-800 tw-underline tw-underline-offset-2 tw-decoration-blue-800/30 hover:tw-decoration-blue-800 tw-transition tw-outline-none focus-visible:tw-outline-focus tw-rounded focus-visible:tw-no-underline'
      >
        {children}
      </BaseLinkTracked>
    </m.div>
  );
}
